import * as XLSX from 'xlsx';

/**
 * Même logique de colonnes / en-têtes que `products.component.ts` (import XLSX produits).
 */
export function normalizeImportHeaderLabel(h: any): string {
    if (h == null || h === '') {
        return '';
    }
    return String(h)
        .trim()
        .replace(/\u00a0/g, ' ')
        .replace(/_/g, ' ')
        .replace(/-/g, ' ')
        .replace(/\s+/g, ' ')
        .toUpperCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '');
}

export interface ProductXlsxParseResult {
    headerMap: Record<string, number>;
    dataRows: any[][];
    missingLabels: string[];
}

/** Colonnes du modèle d'import produit (fichier vide : en-têtes uniquement). */
export const PRODUCT_XLSX_IMPORT_HEADERS: string[] = [
    'NOM',
    'TYPE',
    'PRIX UNITAIRE',
    'REF_INTERNE',
    'REF_FABRICANT',
    'RESULT TTC',
    'RESULT HT',
    'CODE BARRE',
    'TAILLE',
    'COULEUR',
    'RAYON',
    'FAMILLE',
    'SOUS FAMILLE',
    'DESCRIPTION',
    'TVA',
    'TAXE',
    'CATEGORIE',
    'MARQUE',
    'UNITE',
    ...Array.from({ length: 12 }, (_, i) => `PARAM${i + 1}`)
];

export function createEmptyProductImportWorkbook(): XLSX.WorkBook {
    const worksheet = XLSX.utils.aoa_to_sheet([PRODUCT_XLSX_IMPORT_HEADERS]);
    const colWidths = Array.from({ length: PRODUCT_XLSX_IMPORT_HEADERS.length }, () => ({ wch: 16 }));
    colWidths[0] = { wch: 28 };
    colWidths[4] = { wch: 22 };
    colWidths[12] = { wch: 22 };
    worksheet['!cols'] = colWidths;

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template Produits');
    return workbook;
}

export function parseProductXlsxImportSheet(arrayBuffer: ArrayBuffer): ProductXlsxParseResult | { error: string } {
    try {
        const data = new Uint8Array(arrayBuffer);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheet = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheet];
        const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

        const rawHeaders = jsonData[0] ?? [];
        const headers = rawHeaders.map((h: any) => normalizeImportHeaderLabel(h));

        const resolveHeaderIndex = (aliases: string[]): number => {
            const normalizedAliases = aliases
                .map((a) => normalizeImportHeaderLabel(a))
                .filter((a) => a.length > 0);
            for (let i = 0; i < headers.length; i++) {
                const cellH = headers[i];
                if (cellH && normalizedAliases.includes(cellH)) {
                    return i;
                }
            }
            return -1;
        };

        const idxNom = resolveHeaderIndex(['NOM', 'NAME', 'NOM DU PRODUIT', 'LIBELLE']);
        const idxType = resolveHeaderIndex(['TYPE']);
        const idxPrix = resolveHeaderIndex([
            'PRIX UNITAIRE',
            'PRIX',
            'PRIX_UNITAIRE',
            'PRIXUNITAIRE',
            'PU',
            'PRIX DE VENTE'
        ]);
        const idxRefInt = resolveHeaderIndex([
            'RÉFÉRENCE INTERNE',
            'REFERENCE INTERNE',
            'REF INTERNE',
            'REF_INTERNE',
            'REF. INTERNE',
            'REF PRODUIT'
        ]);
        const idxDesc = resolveHeaderIndex(['DESCRIPTION', 'DESCR', 'DETAILS', 'DETAIL']);
        const idxResultTtc = resolveHeaderIndex([
            'RESULT TTC',
            'RESULTAT TTC',
            'RESULT_TTC',
            'RESULT-TTC',
            'PRIX TTC',
            'RESULT T.T.C'
        ]);
        const idxResultHt = resolveHeaderIndex([
            'RESULT HT',
            'RESULTAT HT',
            'RESULT_HT',
            'RESULT-HT',
            'PRIX HT',
            'RESULT H.T'
        ]);

        const missingLabels: string[] = [];
        if (idxNom < 0) {
            missingLabels.push('nom');
        }
        if (idxType < 0) {
            missingLabels.push('type');
        }
        if (idxPrix < 0) {
            missingLabels.push('prix unitaire (ou prix)');
        }
        if (idxRefInt < 0) {
            missingLabels.push('ref_interne');
        }
        if (missingLabels.length > 0) {
            return {
                headerMap: {},
                dataRows: [],
                missingLabels
            };
        }

        const headerMap: Record<string, number> = {
            nom: idxNom,
            type: idxType,
            prix: idxPrix,
            ref_interne: idxRefInt,
            description: idxDesc,
            result_ttc: idxResultTtc,
            result_ht: idxResultHt,
            ref_fabricant: resolveHeaderIndex([
                'RÉFÉRENCE FABRICANT',
                'REFERENCE FABRICANT',
                'RÉFÉRENCE FABRIQUANT',
                'REFERENCE FABRIQUANT',
                'REF FABRICANT',
                'REF FABRIQUANT',
                'REF_FABRICANT',
                'REF_FABRIQUANT'
            ]),
            code_barre: resolveHeaderIndex(['CODE BARRE', 'CODE_BARRE', 'CODE-BARRE', 'CODEBARRE', 'EAN']),
            couleur: resolveHeaderIndex(['COULEUR']),
            taille: resolveHeaderIndex(['TAILLE']),
            rayon: resolveHeaderIndex(['RAYON']),
            famille: resolveHeaderIndex(['FAMILLE']),
            sous_famille: resolveHeaderIndex(['SOUS FAMILLE', 'SOUS_FAMILLE', 'SOUS-FAMILLE', 'SOUS FAMILLE']),
            tva: resolveHeaderIndex([
                'TVA',
                'TAUX TVA',
                'TAUX DE TVA',
                'TAUX',
                'POURCENTAGE TVA',
                'VALEUR TVA',
                'TVA %',
                '% TVA',
                'TAUX T V A'
            ]),
            taxe: resolveHeaderIndex(['TAXE', 'TAXES']),
            marque: resolveHeaderIndex(['MARQUE']),
            categorie: resolveHeaderIndex([
                'CATEGORIE',
                'CATÉGORIE',
                'CATEGORY',
                'CATEGORIE PRODUIT'
            ]),
            unite: resolveHeaderIndex(['UNITE', 'UNITÉ', 'UNIT', 'UNITÉ DE VENTE']),
            quantite: resolveHeaderIndex(['QUANTITE', 'QUANTITÉ', 'QTE', 'QTY', 'QUANTITY'])
        };
        for (let p = 1; p <= 12; p++) {
            headerMap[`param${p}`] = resolveHeaderIndex([`PARAM${p}`]);
        }

        const dataRows = jsonData.slice(1).filter((row) => row.some((cell: any) => cell != null && cell !== ''));

        return { headerMap, dataRows, missingLabels: [] };
    } catch (e: any) {
        return { error: e?.message || String(e) };
    }
}

export function readImportCellTrimmed(row: any[], headerMap: Record<string, number>, key: string): string | null {
    const idx = headerMap[key];
    if (idx == null || idx < 0) {
        return null;
    }
    const v = row[idx];
    if (v === undefined || v === null || v === '') {
        return null;
    }
    return String(v).trim();
}

export function parseTvaImportValue(raw: string): number | null {
    const cleaned = String(raw)
        .trim()
        .replace(/%/g, '')
        .replace(/\s/g, '')
        .replace(',', '.');
    if (cleaned === '') {
        return null;
    }
    const n = parseFloat(cleaned);
    return isNaN(n) ? null : n;
}

function preparePriceListsForImport(
    prix_ht: number,
    prix_ttc: number,
    clientId: number,
    stationId: number
): any {
    return {
        prix_id: 1,
        bascul_calcul: 'prix_public',
        regles: 'fixe',
        montant: Number(prix_ht),
        result_ttc: Number(prix_ttc),
        result_ht: Number(prix_ht),
        client_id: clientId,
        station_id: stationId
    };
}

function prepareWarehousesForImport(
    stock_minimum: number,
    stock_maximum: number,
    clientId: number,
    stationId: number
): any[] {
    return [
        {
            entrepot_id: 1,
            minimum: stock_minimum,
            maximum: stock_maximum,
            is_active_alert: stock_minimum > 0,
            client_id: clientId,
            station_id: stationId
        }
    ];
}

/** Alignement payload POST produit (identique à `products.component.applyImportProductApiShape`). */
export function applyDocumentImportProductApiShape(
    product: Record<string, any>,
    clientId: number,
    stationId: number
): void {
    const tid = product.tva_id;
    if (tid != null && tid !== '' && !Number.isNaN(Number(tid))) {
        product.tva_id = Math.floor(Number(tid));
    }

    if (!Array.isArray(product.taxes)) {
        const tx = product.taxe_id;
        if (tx != null && Number(tx) > 0) {
            product.taxes = [
                {
                    taxe_id: Number(tx),
                    client_id: product.client_id,
                    station_id: product.station_id
                }
            ];
        } else {
            product.taxes = [];
        }
    }

    if (!Array.isArray(product.liste_prix) || product.liste_prix.length === 0) {
        const pu = Number(product.prix_unitaires ?? 0);
        const rht = product.result_ht != null ? Number(product.result_ht) : null;
        const rtc = product.result_ttc != null ? Number(product.result_ttc) : null;
        if (pu > 0 || (rht != null && !Number.isNaN(rht)) || (rtc != null && !Number.isNaN(rtc))) {
            const ht = rht != null && !Number.isNaN(rht) ? rht : pu;
            const ttc = rtc != null && !Number.isNaN(rtc) ? rtc : pu > 0 ? pu : ht;
            product.liste_prix = [preparePriceListsForImport(ht, ttc, clientId, stationId)];
        } else if (product.tva_id != null && Number(product.tva_id) > 0) {
            const ht = Number(product.result_ht ?? 0);
            const ttc = Number(product.result_ttc ?? ht);
            product.liste_prix = [preparePriceListsForImport(ht, ttc, clientId, stationId)];
        } else {
            product.liste_prix = [];
        }
    }

    if (!Array.isArray(product.information_logistique)) {
        const typeNorm = String(product.type || '')
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '');
        const isMateriel = typeNorm === 'materiel';
        product.information_logistique = isMateriel
            ? prepareWarehousesForImport(0, 0, clientId, stationId)
            : [];
    }

    if (product.autoriser_vente_stock_vide === undefined) {
        product.autoriser_vente_stock_vide = false;
    }
    if (product.produit_avec_numero_serie === undefined) {
        product.produit_avec_numero_serie = false;
    }
}
